<script setup lang="ts">
    import Invoice from '@/app/invoicing/models/Invoice';
    import { ModelType } from '@/app/base/schemas/BaseSchema';
    import ModelFileSchema from '@/app/common/schemas/ModelFileSchema';
    import EndpointFactory from '@/app/factories/EndpointFactory';
    import { makeId } from '~/utils/string-helpers';
    import useValidFileFormat from '~/composables/useValidFileFormat';
    import Expense from '~/app/invoicing/models/Expense';

    const props = withDefaults(
        defineProps<{
            model: Invoice | Expense;
            withUpload?: boolean;
        }>(),
        {
            withUpload: true,
        }
    );
    const uploadFileInput = ref<HTMLInputElement | null>(null);
    const inputFileId = ref(makeId());
    const uploading = ref(false);
    const refreshKey = ref(0);

    useListen('common:model-file:deleted', (id) => {
        const fileId = typeof id === 'string' ? id : id?.getId?.();
        if (fileId) {
            props.model.modelFiles?.remove(fileId);
        }
        refreshKey.value++;
    });

    const onFileUpload = async (event: Event) => {
        event.preventDefault();
        const inputElement = event.target as HTMLInputElement;
        const { validateFormat } = useValidFileFormat();
        await validateFormat(inputElement.files, async (validFiles) => {
            uploading.value = true;

            try {
                const promises = validFiles.map((file) => uploadFile(file));
                await Promise.all(promises);
            } finally {
                uploadFileInput.value!.value = '';
                uploading.value = false;
            }
        });
    };

    const uploadFile = async (file: File) => {
        try {
            const endpoint = EndpointFactory.make(ModelType.MODEL_FILES);
            const schema = ModelFileSchema.make({ attributes: { title: file.name } });
            schema.addToOneRelationship('model', props.model.type, props.model.getId());

            const response = await endpoint.upload(schema, file);
            if (response.data) {
                props.model.modelFiles?.add(response.data);
                refreshKey.value++;
            }
            useToasteoSuccess('toasts.upload_success');
            return response.data;
        } catch (error) {
            useToasteoError('toasts.upload_failed');
            return null;
        }
    };
</script>

<template>
    <div :key="refreshKey">
        <div
            v-if="model.modelFiles?.isNotEmpty()"
            class="flex flex-col gap-1 mb-4"
        >
            <div
                v-for="modelFile in model.modelFiles.get()"
                :key="modelFile.getInternalId()"
                class="p-2 text-xs text-gray-600 transition-all border border-gray-100 rounded cursor-pointer hover:text-branding-rich-black hover:bg-neutral-50"
                @click.prevent="modelFile.preview()"
                @contextmenu="modelFile.openContextMenu($event)"
            >
                <span>{{ modelFile.title }}</span>
            </div>
        </div>
        <label
            v-if="withUpload"
            :for="inputFileId"
            class="relative flex items-center gap-2 text-sm transition-all cursor-pointer text-branding-dark-grey hover:text-branding-rich-black hover:underline"
        >
            <Loader v-if="uploading" />
            <i class="fa-regular fa-link" />
            {{ $tc('misc.files_linked_count', model?.modelFiles?.count() ?? 0, { count: model?.modelFiles?.count() }) }}
            <input
                :id="inputFileId"
                ref="uploadFileInput"
                multiple
                class="hidden"
                :name="inputFileId"
                type="file"
                @change="onFileUpload"
            />
        </label>
    </div>
</template>
